import React, { useState, useEffect } from 'react';
import { ProForm, ProFormText, ProFormTextArea, ProFormSwitch } from '@ant-design/pro-components';
import { Card, Statistic, Row, Col, message, Button, Space, Divider } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useFilterSettings, useFilterStats, useUpdateFilterSettings } from '../api';

interface FilterSettings {
    id: string;
    hashtags: string;
    mentions: string;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
}

interface FilterStats {
    totalProcessed: number;
    matchedFilter: number;
    didNotMatch: number;
    filterMatchRate: number;
    totalSaved: number;
    storageEfficiency: number;
}

const FilterSettingsTab: React.FC = () => {
    const queryClient = useQueryClient();
    const [form] = ProForm.useForm();

    // Fetch filter settings and statistics using API hooks
    const { data: settingsResponse, isLoading: settingsLoading } = useFilterSettings();
    const { data: statsResponse, isLoading: statsLoading } = useFilterStats();
    const updateSettingsMutation = useUpdateFilterSettings();

    const handleSubmit = async (values: any) => {
        await updateSettingsMutation.mutateAsync(values);
    };

    const handleRefreshStats = () => {
        queryClient.invalidateQueries({ queryKey: ['instagram-filter-stats'] });
        message.success('Statistics refreshed');
    };

    const settings: FilterSettings | undefined = settingsResponse?.data;
    const stats: FilterStats | undefined = statsResponse?.data;

    // Update form when settings data loads
    useEffect(() => {
        console.log('Settings response:', settingsResponse);
        console.log('Settings data:', settings);
        if (settings) {
            console.log('Setting form values:', {
                hashtags: settings.hashtags || '',
                mentions: settings.mentions || '',
                enabled: settings.enabled || false,
            });
            form.setFieldsValue({
                hashtags: settings.hashtags || '',
                mentions: settings.mentions || '',
                enabled: settings.enabled || false,
            });
        }
    }, [settings, form, settingsResponse]);

    return (
        <div>
            <Row gutter={[16, 16]}>
                {/* Statistics Cards */}
                <Col span={24}>
                    <Card
                        title="Content Filtering Statistics"
                        extra={
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefreshStats}
                                loading={statsLoading}
                            >
                                Refresh
                            </Button>
                        }
                    >
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title="Total Processed"
                                    value={stats?.totalProcessed || 0}
                                    precision={0}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Matched Filter"
                                    value={stats?.matchedFilter || 0}
                                    precision={0}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Filter Match Rate"
                                    value={stats?.filterMatchRate || 0}
                                    precision={1}
                                    suffix="%"
                                    valueStyle={{ color: stats?.filterMatchRate > 10 ? '#52c41a' : '#faad14' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Storage Efficiency"
                                    value={stats?.storageEfficiency || 0}
                                    precision={1}
                                    suffix="%"
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Filter Settings Form */}
                <Col span={24}>
                    <Card title="Global Content Filtering Settings" loading={settingsLoading}>
                        <ProForm
                            form={form}
                            onFinish={handleSubmit}
                            submitter={{
                                render: (props, dom) => (
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            loading={updateSettingsMutation.isLoading}
                                            onClick={() => props.submit?.()}
                                        >
                                            Save Settings
                                        </Button>
                                    </Space>
                                ),
                            }}
                            layout="horizontal"
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                        >
                            <ProFormSwitch
                                name="enabled"
                                label="Enable Filtering"
                                checkedChildren="Enabled"
                                unCheckedChildren="Disabled"
                                extra="When enabled, only content matching the hashtags or mentions below will be saved"
                            />

                            <Divider />

                            <ProFormTextArea
                                name="hashtags"
                                label="Hashtags"
                                placeholder="#sponsored, #ad, #partnership, #collab"
                                fieldProps={{
                                    rows: 3,
                                    showCount: true,
                                }}
                                extra="Comma-separated hashtags to match (case-insensitive). Example: #sponsored, #ad, #partnership"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            const hashtags = value.split(',').map((h: string) => h.trim());
                                            const invalidHashtags = hashtags.filter((h: string) => h && !h.startsWith('#'));
                                            if (invalidHashtags.length > 0) {
                                                return Promise.reject(new Error('All hashtags must start with #'));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            />

                            <ProFormTextArea
                                name="mentions"
                                label="Mentions"
                                placeholder="@yourbrand, @yourcompany, @yourhandle"
                                fieldProps={{
                                    rows: 3,
                                    showCount: true,
                                }}
                                extra="Comma-separated mentions to match (case-insensitive). Example: @yourbrand, @yourcompany"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            const mentions = value.split(',').map((m: string) => m.trim());
                                            const invalidMentions = mentions.filter((m: string) => m && !m.startsWith('@'));
                                            if (invalidMentions.length > 0) {
                                                return Promise.reject(new Error('All mentions must start with @'));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            />
                        </ProForm>

                        <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
                            <h4>How Filtering Works:</h4>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>Content captions are checked against hashtags and mentions</li>
                                <li>If ANY hashtag or mention matches, the content is saved</li>
                                <li>Matching is case-insensitive and uses exact string matching</li>
                                <li>When filtering is disabled, ALL content is saved</li>
                                <li>Stories without captions won't match hashtag/mention filters</li>
                            </ul>
                        </Card>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FilterSettingsTab;